import { CFA_LEVELS, DEFAULT_LEVEL, normalizeLevel } from '../constants/levels';
import {
  L1_ANNUAL_SKU,
  L1_PRODUCT,
  LIFETIME_UNTIL,
  PACKAGE_LIFETIME_SKU,
  PACKAGE_PRICE_USD,
  getSku,
  quoteSku,
} from '../constants/skus';
import { isPassActive, normalizePaidUntil } from './access';

export function emptyEntitlements() {
  return {
    l1: { paidUntil: '', term: null, sku: '', amountUsd: null, deferred: false, accessDays: null, subscriptionId: '', subscriptionStatus: '' },
    l2: { paidUntil: '', term: null, sku: '', amountUsd: null, deferred: false, accessDays: null, subscriptionId: '', subscriptionStatus: '' },
    l3: { paidUntil: '', term: null, sku: '', amountUsd: null, deferred: false, accessDays: null, subscriptionId: '', subscriptionStatus: '' },
    packageLifetime: false,
    packageSku: '',
    packagePurchasedAt: '',
  };
}

function cancellationFieldsFromRow(raw = {}) {
  const fields = {};
  if (raw.cancelAtPeriodEnd != null) fields.cancelAtPeriodEnd = !!raw.cancelAtPeriodEnd;
  if (raw.cancelAt) fields.cancelAt = String(raw.cancelAt);
  if (raw.cancellationFeedback) fields.cancellationFeedback = String(raw.cancellationFeedback);
  if (raw.cancellationComment) fields.cancellationComment = String(raw.cancellationComment);
  if (raw.cancellationRequestedAt) {
    fields.cancellationRequestedAt = raw.cancellationRequestedAt;
  }
  return fields;
}

function asLevelRow(raw) {
  if (!raw || typeof raw !== 'object') {
    return { paidUntil: '', term: null, sku: '', amountUsd: null, deferred: false, accessDays: null, subscriptionId: '', subscriptionStatus: '' };
  }
  const paidUntil =
    raw.paidUntil === LIFETIME_UNTIL || raw.paidUntil === 'deferred'
      ? raw.paidUntil
      : normalizePaidUntil(raw.paidUntil);
  const deferred = raw.deferred === true || paidUntil === 'deferred';
  return {
    paidUntil,
    term: raw.term || (paidUntil === LIFETIME_UNTIL ? 'lifetime' : paidUntil || deferred ? 'annual' : null),
    sku: raw.sku || '',
    amountUsd:
      raw.amountUsd == null || raw.amountUsd === ''
        ? null
        : Number.isFinite(Number(raw.amountUsd))
          ? Number(raw.amountUsd)
          : null,
    deferred,
    accessDays: raw.accessDays == null ? (deferred ? 365 : null) : Number(raw.accessDays) || null,
    subscriptionId: raw.subscriptionId || '',
    subscriptionStatus: raw.subscriptionStatus || '',
    ...cancellationFieldsFromRow(raw),
  };
}

/** Merge legacy `paidUntil` into the per-level map so old L1 buyers keep access. */
export function resolveEntitlements(profile = {}) {
  const stored = profile?.entitlements && typeof profile.entitlements === 'object' ? profile.entitlements : {};
  const next = emptyEntitlements();
  CFA_LEVELS.forEach((level) => {
    next[level] = asLevelRow(stored[level]);
  });
  next.packageLifetime = stored.packageLifetime === true || stored.packageLifetime === '1';
  next.packageSku = stored.packageSku || '';
  next.packagePurchasedAt = stored.packagePurchasedAt || '';

  const legacy = normalizePaidUntil(profile.paidUntil);
  if (legacy && !next.l1.paidUntil) {
    next.l1 = {
      paidUntil: legacy,
      term: legacy === LIFETIME_UNTIL ? 'lifetime' : 'annual',
      sku: stored.l1?.sku || L1_PRODUCT,
      amountUsd:
        profile.paidAmountUsd == null || profile.paidAmountUsd === ''
          ? null
          : Number.isFinite(Number(profile.paidAmountUsd))
            ? Number(profile.paidAmountUsd)
            : null,
    };
  }
  if (legacy === LIFETIME_UNTIL || profile.packageLifetime === true) {
    next.packageLifetime = true;
  }
  return next;
}

export function hasPackageLifetime(profileOrEntitlements = {}) {
  const entitlements = profileOrEntitlements.l1
    ? profileOrEntitlements
    : resolveEntitlements(profileOrEntitlements);
  return entitlements.packageLifetime === true;
}

/** Referral free week rows (server: netlify/functions/_entitlements.js). Trial access, not a purchase. */
export const REFERRAL_WEEK_TERM = 'referral-week';
export const isReferralWeekRow = (row) => row?.term === REFERRAL_WEEK_TERM;

export function hasLevelAccess(profileOrEntitlements, level = DEFAULT_LEVEL) {
  const entitlements =
    profileOrEntitlements?.l1 && profileOrEntitlements.l2
      ? profileOrEntitlements
      : resolveEntitlements(profileOrEntitlements);
  if (entitlements.packageLifetime) return true;
  const row = entitlements[normalizeLevel(level)];
  if (!row) return false;
  if (row.paidUntil === LIFETIME_UNTIL || row.term === 'lifetime') return true;
  if (row.deferred || row.paidUntil === 'deferred') return true;
  return isPassActive(row.paidUntil);
}

/** Access the account paid for (package, lifetime, active pass), excluding a referral free week. */
export function hasPaidLevelAccess(profileOrEntitlements, level = DEFAULT_LEVEL) {
  const entitlements =
    profileOrEntitlements?.l1 && profileOrEntitlements.l2
      ? profileOrEntitlements
      : resolveEntitlements(profileOrEntitlements);
  if (entitlements.packageLifetime) return true;
  const row = entitlements[normalizeLevel(level)];
  if (!row || isReferralWeekRow(row)) return false;
  return hasLevelAccess(entitlements, level);
}

/** Whole days left in an active referral free week on a level (0 when none). Ends 23:59:59 UTC on paidUntil. */
export function freeWeekDaysLeft(profileOrEntitlements, level = DEFAULT_LEVEL, now = Date.now()) {
  const entitlements =
    profileOrEntitlements?.l1 && profileOrEntitlements.l2
      ? profileOrEntitlements
      : resolveEntitlements(profileOrEntitlements);
  const row = entitlements[normalizeLevel(level)];
  if (!isReferralWeekRow(row) || hasPaidLevelAccess(entitlements, level)) return 0;
  const raw = normalizePaidUntil(row.paidUntil);
  const end = raw ? Date.parse(`${raw}T23:59:59.000Z`) : NaN;
  if (!Number.isFinite(end) || end < now) return 0;
  return Math.max(1, Math.ceil((end - now) / 86400000));
}

export function formatEntitlementUntil(paidUntil) {
  const raw =
    paidUntil === LIFETIME_UNTIL || paidUntil === 'deferred'
      ? paidUntil
      : normalizePaidUntil(paidUntil);
  if (raw === LIFETIME_UNTIL) return 'Lifetime';
  if (raw === 'deferred') return 'When mocks ship';
  return raw || '';
}

export function packageCreditUsd(profile = {}) {
  const entitlements = resolveEntitlements(profile);
  if (entitlements.packageLifetime) return PACKAGE_PRICE_USD;
  // Only webhook/Admin-stamped spend fields are trusted for money (not entitlements.*.amountUsd).
  let credit = 0;
  if (hasPaidLevelAccess(entitlements, 'l1')) {
    const v = Number(profile.paidAmountUsd);
    if (Number.isFinite(v) && v > 0) credit += v;
  }
  if (hasPaidLevelAccess(entitlements, 'l2')) {
    const v = Number(profile.paidAmountL2Usd);
    if (Number.isFinite(v) && v > 0) credit += v;
  }
  if (hasPaidLevelAccess(entitlements, 'l3')) {
    const v = Number(profile.paidAmountL3Usd);
    if (Number.isFinite(v) && v > 0) credit += v;
  }
  return Math.min(PACKAGE_PRICE_USD, Math.max(0, credit));
}

export function quoteForProfile(skuValue, profile = {}, now = new Date()) {
  const sku = getSku(skuValue);
  const creditUsd = sku.id === PACKAGE_LIFETIME_SKU ? packageCreditUsd(profile) : 0;
  return quoteSku(sku.id, { now, creditUsd });
}

export function ownsSku(profile, skuValue) {
  const sku = getSku(skuValue);
  if (sku.id === PACKAGE_LIFETIME_SKU) return hasPackageLifetime(profile);
  const entitlements = resolveEntitlements(profile);
  if (entitlements.packageLifetime) return true;
  return sku.levels.every((level) => {
    const row = entitlements[level];
    if (!hasPaidLevelAccess(entitlements, level)) return false;
    if (sku.term === 'monthly') return true;
    if (sku.term === 'lifetime') return row.term === 'lifetime';
    return row.sku === sku.id;
  });
}

export function applySkuGrant(
  existingProfile = {},
  skuValue,
  { paidUntil, paidAt, priceUsd, subscriptionId = '', subscriptionStatus = '' } = {}
) {
  const sku = getSku(skuValue);
  const entitlements = resolveEntitlements(existingProfile);
  const grantedAt = paidAt || new Date().toISOString();
  const amountUsd =
    priceUsd == null || priceUsd === ''
      ? null
      : Number.isFinite(Number(priceUsd))
        ? Number(priceUsd)
        : null;

  // Package only — per-level SKUs may also use term "lifetime" without unlocking other levels.
  if (sku.id === PACKAGE_LIFETIME_SKU) {
    entitlements.packageLifetime = true;
    entitlements.packageSku = sku.id;
    entitlements.packagePurchasedAt = grantedAt;
    CFA_LEVELS.forEach((level) => {
      entitlements[level] = {
        paidUntil: LIFETIME_UNTIL,
        term: 'lifetime',
        sku: sku.id,
        amountUsd: level === 'l1' ? amountUsd : entitlements[level].amountUsd,
        deferred: false,
        accessDays: null,
      };
    });
  } else {
    const deferred = paidUntil === 'deferred' || (!sku.contentReady && !paidUntil);
    sku.levels.forEach((level) => {
      if (
        sku.term === 'monthly' &&
        entitlements[level].term !== 'monthly' &&
        hasPaidLevelAccess(entitlements, level)
      ) return;
      entitlements[level] = {
        paidUntil: deferred
          ? 'deferred'
          : sku.term === 'lifetime'
            ? LIFETIME_UNTIL
            : paidUntil || entitlements[level].paidUntil,
        term: sku.term,
        sku: sku.id,
        amountUsd,
        deferred,
        accessDays: sku.term === 'annual' ? 365 : null,
        subscriptionId: sku.term === 'monthly' ? subscriptionId : '',
        subscriptionStatus: sku.term === 'monthly' ? subscriptionStatus : '',
      };
    });
  }

  const patch = {
    entitlements,
    lastSku: sku.id,
    lastProduct: sku.product,
  };
  if (sku.levels.includes('l1') || sku.id === PACKAGE_LIFETIME_SKU) {
    const until = entitlements.l1.paidUntil || paidUntil || '';
    if (until && until !== 'deferred') patch.paidUntil = until;
  }
  return patch;
}

export function accessFlagsFromProfile(profile = {}) {
  const entitlements = resolveEntitlements(profile);
  return {
    entitlements,
    paid: hasLevelAccess(entitlements, 'l1'),
    paidUntil: entitlements.l1.paidUntil || normalizePaidUntil(profile.paidUntil),
    paidL2: hasLevelAccess(entitlements, 'l2'),
    paidL3: hasLevelAccess(entitlements, 'l3'),
    hasPackage: hasPackageLifetime(entitlements),
    /** Levels open only through a referral free week → whole days left (Admin keeps them out of paid passes). */
    freeWeekDaysLeft: Object.fromEntries(
      ['l1', 'l2', 'l3'].map((level) => [level, freeWeekDaysLeft(entitlements, level)]).filter(([, d]) => d > 0)
    ),
    paidAmountUsd: profile.paidAmountUsd == null ? null : Number(profile.paidAmountUsd) || null,
    paidAmountL2Usd: profile.paidAmountL2Usd == null ? null : Number(profile.paidAmountL2Usd) || null,
    paidAmountL3Usd: profile.paidAmountL3Usd == null ? null : Number(profile.paidAmountL3Usd) || null,
    lastSku: profile.lastSku || L1_ANNUAL_SKU,
  };
}

/** CFA-38: last-known-good profile fields for access when Firestore profile read fails. */
const ENTITLEMENT_CACHE_PREFIX = 'cfa_entitlements_v1_';

function entitlementCacheKey(uid) {
  return `${ENTITLEMENT_CACHE_PREFIX}${uid}`;
}

/** Persist the minimal profile slice needed to rebuild access flags. */
export function cacheEntitlementsForUser(uid, profile = {}) {
  if (!uid || typeof window === 'undefined') return;
  try {
    const payload = {
      savedAt: Date.now(),
      profile: {
        paidUntil: profile.paidUntil || '',
        paidAmountUsd: profile.paidAmountUsd ?? null,
        paidAmountL2Usd: profile.paidAmountL2Usd ?? null,
        paidAmountL3Usd: profile.paidAmountL3Usd ?? null,
        lastSku: profile.lastSku || '',
        packageLifetime: profile.packageLifetime === true,
        entitlements: profile.entitlements && typeof profile.entitlements === 'object' ? profile.entitlements : undefined,
        freeSessionUsed: !!profile.freeSessionUsed,
        freeMockCompletedAt: profile.freeMockCompletedAt || null,
        freeTrialUsed: profile.freeTrialUsed,
        examDate: profile.examDate || '',
        examWindowLabel: profile.examWindowLabel || '',
      },
    };
    window.localStorage.setItem(entitlementCacheKey(uid), JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/** @returns {object|null} cached profile slice, or null */
export function readCachedEntitlementsForUser(uid) {
  if (!uid || typeof window === 'undefined') return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(entitlementCacheKey(uid)) || 'null');
    if (!parsed?.profile || typeof parsed.profile !== 'object') return null;
    return parsed.profile;
  } catch {
    return null;
  }
}

export function clearCachedEntitlementsForUser(uid) {
  if (!uid || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(entitlementCacheKey(uid));
  } catch {
    /* ignore */
  }
}
